import React, { useEffect, useState } from "react";
import { Crown, Utensils } from "lucide-react";
import { useLang, t } from "../lib/i18n";

// "Table Tents" — 6 sweet facts about Anagha, rotated 3 at a time.
const FACTS = [
  { key: "fact1", emoji: "❤️" },
  { key: "fact2", emoji: "🧸" },
  { key: "fact3", emoji: "🎵" },
  { key: "fact4", emoji: "🗣️" },
  { key: "fact5", emoji: "🍌" },
  { key: "fact6", emoji: "🙈" },
];

const VISIBLE = 3;
const ROTATE_MS = 6000;

export default function RoyalBanquet() {
  const lang = useLang();
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setOffset((o) => (o + VISIBLE) % FACTS.length);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, []);

  const visibleFacts = Array.from({ length: VISIBLE }, (_, i) => FACTS[(offset + i) % FACTS.length]);

  return (
    <section
      data-testid="royal-banquet-banner"
      className="absolute top-2 sm:top-3 left-1/2 -translate-x-1/2 z-20 w-[min(94vw,940px)] px-4 sm:px-6 py-3 sm:py-4 rounded-3xl pointer-events-none"
      style={{
        background:
          "linear-gradient(140deg, rgba(74,22,53,0.82) 0%, rgba(43,10,32,0.86) 60%, rgba(20,6,16,0.88) 100%)",
        boxShadow:
          "0 18px 48px -20px rgba(74,22,53,0.65), inset 0 1px 0 rgba(212,175,55,0.28)",
        border: "1px solid rgba(212,175,55,0.35)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-center gap-3 text-center">
        <Crown size={20} className="text-amber-300 shrink-0" />
        <div>
          <p className="text-[0.62rem] sm:text-[0.7rem] tracking-[0.28em] uppercase text-amber-200/85"
             style={{ fontFamily: "'Tiro Telugu','Noto Sans Telugu',serif" }}>
            {t("banquet_eyebrow", lang)}
          </p>
          <h2
            className="text-xl sm:text-2xl md:text-3xl leading-tight"
            style={{
              fontFamily: "'Yatra One','Tiro Telugu','Noto Sans Telugu',serif",
              background: "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 55%,#8A6A1A 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: "0 0 24px rgba(212,175,55,0.25)",
            }}
            data-testid="royal-banquet-title"
          >
            👑 {t("banquet_title", lang)}
          </h2>
        </div>
        <Utensils size={20} className="text-amber-300 shrink-0" />
      </div>

      <p
        className="mt-2 text-center text-[12px] sm:text-sm text-amber-100/85 max-w-2xl mx-auto"
        style={{ fontFamily: "'Tiro Telugu','Noto Sans Telugu',serif" }}
      >
        {t("banquet_sub", lang)}
      </p>

      {/* Table tents */}
      <div className="mt-3 sm:mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
        {visibleFacts.map((f, i) => (
          <TableTent key={`${f.key}-${offset}-${i}`} fact={f} lang={lang} delay={i * 120} />
        ))}
      </div>
    </section>
  );
}

function TableTent({ fact, lang, delay }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(id);
  }, [delay]);

  return (
    <div
      data-testid={`banquet-tent-${fact.key}`}
      className={`relative rounded-2xl px-3 py-2.5 sm:py-3 transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
      }`}
      style={{
        background:
          "linear-gradient(180deg, rgba(255,244,213,0.96) 0%, rgba(250,225,170,0.92) 100%)",
        border: "1px solid rgba(184,120,32,0.45)",
        boxShadow:
          "0 6px 18px -6px rgba(43,10,32,0.55), inset 0 1px 0 rgba(255,255,255,0.6)",
        // Tent fold — top corners slightly tilted
        clipPath:
          "polygon(6% 0%, 94% 0%, 100% 12%, 100% 100%, 0% 100%, 0% 12%)",
      }}
    >
      <div className="flex items-start gap-2">
        <span className="text-xl sm:text-2xl leading-none shrink-0" aria-hidden="true">
          {fact.emoji}
        </span>
        <div className="min-w-0">
          <p
            className="text-[0.6rem] tracking-[0.22em] uppercase text-[#7a3a1a]/80"
            style={{ fontFamily: "'Tiro Telugu','Noto Sans Telugu',serif" }}
          >
            {t("banquet_tent", lang)}
          </p>
          <p
            className="text-[13px] sm:text-sm font-bold text-[#4A1635] leading-snug"
            style={{ fontFamily: "'Yatra One','Tiro Telugu',serif" }}
          >
            {t(`banquet_${fact.key}_l`, lang)}
          </p>
          <p
            className="mt-1 text-[11.5px] sm:text-[12.5px] text-[#5a2b1a] leading-snug"
            style={{ fontFamily: "'Tiro Telugu','Noto Sans Telugu',serif" }}
          >
            {t(`banquet_${fact.key}_v`, lang)}
          </p>
        </div>
      </div>
    </div>
  );
}
